import type { ColumnDef } from "@tanstack/react-table";
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { ScrollArea } from "components/ui/ScrollArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import TablePagination from "components/ui/Table/TablePagination";
import classNames from "components/ui/classNames";
import { useIntl } from "react-intl";
import type { InputMaybe } from "types/graphqlTypes";

interface CompanyCorporateActionsViewProps<T> {
  data: T[];
  columns: (
    | ColumnDef<T, string>
    | ColumnDef<T, number>
    | ColumnDef<T, boolean>
    | ColumnDef<T, Date>
    | ColumnDef<T, InputMaybe<string>>
    | ColumnDef<T, InputMaybe<number>>
    | ColumnDef<T, InputMaybe<Date>>
    | ColumnDef<T, InputMaybe<boolean>>
    | ColumnDef<T, InputMaybe<unknown>>
  )[];
  availableYears: number[];
  filterByYear?: number | null;
  setFilterByYear: (value: number | null) => void;
  isRenderPageDefault?: boolean;
}

export default function CompanyCorporateActionsView<T extends Record<string, unknown>>({
  data,
  columns,
  availableYears,
  filterByYear,
  setFilterByYear,
  isRenderPageDefault = false,
}: CompanyCorporateActionsViewProps<T>) {
  const intl = useIntl();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: isRenderPageDefault ? 10 : 16,
      },
    },
  });

  return (
    <div className="flex h-full w-full flex-col items-end space-y-2">
      <div className="flex w-full items-start">
        <div className="w-full md:w-36">
          <Select
            value={filterByYear ? String(filterByYear) : ""}
            onValueChange={(value) => {
              if (!value) {
                return setFilterByYear(null);
              }

              return setFilterByYear(parseInt(value, 10));
            }}
          >
            <SelectTrigger
              className={classNames(
                "font-aeonik",
                "w-full rounded-lg",
                "border border-slate-200 bg-white",
                "text-sm font-medium tracking-wide text-slate-600",
                "disabled:select-none",
                "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
                "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                "truncate",
              )}
            >
              <SelectValue
                placeholder={intl.formatMessage({
                  defaultMessage: "Filtre o ano",
                  id: "0HA0Sr",
                })}
              />
            </SelectTrigger>
            <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
              <SelectItem value="">
                {filterByYear === null
                  ? intl.formatMessage({
                      defaultMessage: "Filtre o ano",
                      id: "0HA0Sr",
                    })
                  : intl.formatMessage({
                      defaultMessage: "Remover filtro",
                      id: "J5Id85",
                    })}
              </SelectItem>

              {availableYears.map((year) => (
                <SelectItem key={`selectBoxYear/${year}`} value={String(year)} className="w-full">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
        <div className="pb-4 lg:w-full">
          <div
            className={classNames("relative overflow-auto rounded-xl border border-slate-200", "dark:border-slate-950")}
          >
            <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                  >
                    {headerGroup.headers
                      .filter((header) => !header.column.columnDef.meta?.isHidden)
                      .map((header) => {
                        return (
                          <th
                            key={header.id}
                            className={classNames(
                              "m-0 bg-slate-50 p-0 text-center",
                              header.column.columnDef.meta?.header?.className,
                            )}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        );
                      })}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {table.getRowModel().rows.map((row) => {
                  return (
                    <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                      {row
                        .getVisibleCells()
                        .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                        .map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              className={classNames("m-0 p-0 text-center", cell.column.columnDef.meta?.cell?.className)}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </ScrollArea>

      <TablePagination<T> table={table} />
    </div>
  );
}
